import { useCallback } from 'react';
import { isPerlinEffect } from '../selectors';
import type { EffectConfiguration } from '../TextParticles.types';
import { TestIds } from '../../constants';

export const useTextHandlers = ({
  effectConfiguration,
  isAnimationReady,
  applyReducedMotion,
  handlePause,
  handlePlay,
  pauseOnHover,
  canvasRef,
}: {
  effectConfiguration: EffectConfiguration | null;
  isAnimationReady: boolean;
  applyReducedMotion: boolean;
  handlePause: () => Promise<void>;
  handlePlay: () => Promise<void>;
  pauseOnHover: boolean;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
}) => {
  const onTextClick = useCallback(async () => {
    if (
      !effectConfiguration ||
      !isPerlinEffect(effectConfiguration) ||
      !isAnimationReady ||
      applyReducedMotion
    ) {
      return;
    }

    const isPlaying =
      canvasRef.current?.getAttribute(TestIds.viewer.animationState) ===
      'playing';

    if (isPlaying) {
      await handlePause();
    } else {
      await handlePlay();
    }
  }, [
    effectConfiguration,
    isAnimationReady,
    applyReducedMotion,
    canvasRef,
    handlePlay,
    handlePause,
  ]);
  const onMouseEnterText = useCallback(async () => {
    if (
      !effectConfiguration ||
      !isPerlinEffect(effectConfiguration) ||
      !pauseOnHover ||
      !isAnimationReady ||
      applyReducedMotion
    ) {
      return;
    }

    const isPlaying =
      canvasRef.current?.getAttribute(TestIds.viewer.animationState) ===
      'playing';

    if (isPlaying) {
      await handlePause();
    }
  }, [
    effectConfiguration,
    pauseOnHover,
    isAnimationReady,
    applyReducedMotion,
    canvasRef,
    handlePause,
  ]);

  const onMouseLeaveText = useCallback(async () => {
    if (
      !effectConfiguration ||
      !isPerlinEffect(effectConfiguration) ||
      !pauseOnHover ||
      !isAnimationReady ||
      applyReducedMotion
    ) {
      return;
    }

    const isPaused =
      canvasRef.current?.getAttribute(TestIds.viewer.animationState) ===
      'paused';

    if (isPaused) {
      await handlePlay();
    }
  }, [
    effectConfiguration,
    pauseOnHover,
    isAnimationReady,
    applyReducedMotion,
    canvasRef,
    handlePlay,
  ]);

  return {
    onMouseEnterText,
    onMouseLeaveText,
    onTextClick,
  };
};
